import React from 'react';

import type {
  EventConfirmationRecord,
  EventConfirmationState,
} from '../../api/events';
import './EventConfirmation.css';

export type EventClientAcceptanceEvidence = {
  accepted_at: string;
  accepted_client_email: string | null;
  signature_method: string | null;
  accepted_version_number?: number | null;
  quote_snapshot_hash?: string | null;
  snapshot_schema_version?: string | null;
} | null;

type EventConfirmationEvidenceProps = {
  confirmation: EventConfirmationState | null;
  eventStatus?: string | null;
  clientAcceptance?: EventClientAcceptanceEvidence;
  loading?: boolean;
  error?: string | null;
  compact?: boolean;
  className?: string;
};

type EvidenceFact = {
  label: string;
  value: string;
  title?: string;
};

export type EventConfirmationEvidenceView = {
  kind: 'client' | 'direct' | 'manager' | 'r2' | 'pending' | 'historical' | 'legacy' | 'required' | 'unavailable';
  eyebrow: string;
  title: string;
  detail: string;
  facts: EvidenceFact[];
};

const FIRM_EVENT_STATUSES = new Set([
  'confirmed',
  'picking',
  'partially_filled',
  'filled',
  'partially_shipped',
  'shipped',
  'on_rent',
  'partially_returned',
  'returning',
  'returned',
  'ready_to_invoice',
  'invoiced',
  'closed',
]);

function normalize(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function isClientConfirmationRecord(record: EventConfirmationRecord | null | undefined): boolean {
  if (!record) return false;
  const method = normalize(record.method);
  return (
    method.includes('client')
    || method.includes('signature')
    || method.includes('acceptance')
    || Boolean(record.signatureRecordId)
    || Boolean(record.eventOrderAcceptanceId)
    || Boolean(record.acceptanceId)
  );
}

function formatDateTime(
  value: string | null | undefined,
  timeZone?: string | null,
): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  try {
    return parsed.toLocaleString('en-US', timeZone ? { timeZone } : undefined);
  } catch {
    // Preserve a useful date when an imported legacy timezone is not supported
    // by the browser's Intl implementation.
    return parsed.toLocaleString('en-US');
  }
}

function formatImportedDateTime(
  confirmedAt: string | null | undefined,
  sourceLocalAt: string | null | undefined,
  sourceTimeZone: string | null | undefined,
): string | null {
  if (confirmedAt) return formatDateTime(confirmedAt, sourceTimeZone);
  if (!sourceLocalAt) return null;

  // R2 source timestamps are timezone-naive wall-clock values. When no
  // normalized instant was retained, format those components without letting
  // the browser reinterpret them in its own local timezone.
  const naiveMatch = sourceLocalAt.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/,
  );
  if (naiveMatch) {
    const [, year, month, day, hour, minute, second = '0'] = naiveMatch;
    const wallClock = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ));
    return wallClock.toLocaleString('en-US', { timeZone: 'UTC' });
  }

  return formatDateTime(sourceLocalAt, sourceTimeZone);
}

function pushFact(facts: EvidenceFact[], label: string, value: string | null | undefined, title?: string) {
  if (!value) return;
  facts.push({ label, value, title });
}

function clientAcceptanceView(
  acceptance: NonNullable<EventClientAcceptanceEvidence>,
  record?: EventConfirmationRecord | null,
): EventConfirmationEvidenceView {
  const facts: EvidenceFact[] = [];
  pushFact(facts, 'Signed', formatDateTime(acceptance.accepted_at));
  pushFact(facts, 'Client', acceptance.accepted_client_email);
  pushFact(facts, 'Signature', acceptance.signature_method);
  if (Number.isFinite(Number(acceptance.accepted_version_number))) {
    pushFact(facts, 'Quote', `Version ${Number(acceptance.accepted_version_number)}`);
  }
  if (acceptance.quote_snapshot_hash) {
    pushFact(
      facts,
      'Snapshot',
      `${acceptance.quote_snapshot_hash.slice(0, 12)}...`,
      acceptance.quote_snapshot_hash,
    );
  }
  pushFact(facts, 'Snapshot format', acceptance.snapshot_schema_version);
  if (record?.inventoryCommitmentSeq != null) {
    pushFact(facts, 'Commitment order', `#${record.inventoryCommitmentSeq}`);
  }
  return {
    kind: 'client',
    eyebrow: 'Client evidence',
    title: 'Client signed',
    detail: 'The client accepted this quote and the signed quote snapshot is retained as evidence.',
    facts,
  };
}

export function buildEventConfirmationEvidenceView(params: {
  confirmation: EventConfirmationState | null;
  eventStatus?: string | null;
  clientAcceptance?: EventClientAcceptanceEvidence;
  loading?: boolean;
  error?: string | null;
}): EventConfirmationEvidenceView {
  const { confirmation, clientAcceptance } = params;
  const status = normalize(confirmation?.eventStatus ?? params.eventStatus);
  const firm = FIRM_EVENT_STATUSES.has(status);
  const record = confirmation?.latestRecord;

  if (
    clientAcceptance
    && (
      (record?.isCurrent && isClientConfirmationRecord(record))
      || (firm && confirmation?.clientSignaturePresent === true)
    )
  ) {
    return clientAcceptanceView(clientAcceptance, confirmation?.latestRecord);
  }

  const pending = confirmation?.pendingRequest;
  if (pending) {
    const facts: EvidenceFact[] = [];
    pushFact(facts, 'Requested', formatDateTime(pending.requestedAt));
    pushFact(facts, 'Requested by', pending.requestedByName);
    pushFact(facts, 'Reason', pending.reason);
    return {
      kind: 'pending',
      eyebrow: 'Manager review',
      title: 'Confirmation approval pending',
      detail: 'No client signature is recorded. Inventory is not committed until an authorized manager approves this request.',
      facts,
    };
  }

  if (params.loading && !confirmation) {
    return {
      kind: 'unavailable',
      eyebrow: 'Confirmation policy',
      title: 'Checking confirmation requirements',
      detail: 'Loading the location policy and any existing confirmation evidence.',
      facts: [],
    };
  }

  if (params.error && !confirmation) {
    return {
      kind: 'unavailable',
      eyebrow: 'Confirmation policy',
      title: 'Confirmation details unavailable',
      detail: 'The policy could not be loaded. Open the confirmation dialog again before changing this event to Confirmed.',
      facts: [],
    };
  }

  if (record) {
    const method = normalize(record.method);

    if (method.includes('r2') || method.includes('import')) {
      const facts: EvidenceFact[] = [];
      pushFact(
        facts,
        'Converted in R2',
        formatImportedDateTime(
          record.confirmedAt,
          record.r2ConvertedAt,
          record.sourceTimezone,
        ),
      );
      return {
        kind: record.isCurrent ? 'r2' : 'historical',
        eyebrow: record.isCurrent ? 'Imported confirmation' : 'Prior imported confirmation',
        title: record.isCurrent ? 'Confirmed in R2' : 'Previously confirmed in R2',
        detail: record.isCurrent
          ? 'No client signature is recorded in this app.'
          : 'This prior R2 confirmation no longer reserves inventory.',
        facts,
      };
    }

    const commonFacts: EvidenceFact[] = [];
    pushFact(commonFacts, 'Confirmed', formatDateTime(record.confirmedAt ?? record.recordedAt));
    pushFact(commonFacts, 'Confirmed by', record.confirmedByName);
    pushFact(commonFacts, 'Reason', record.reason);
    if (record.quoteVersionNumber != null) {
      pushFact(commonFacts, 'Quote', `Version ${record.quoteVersionNumber}`);
    }
    pushFact(commonFacts, 'Snapshot format', record.quoteSnapshotVersion);
    if (record.quoteSnapshotHash) {
      pushFact(commonFacts, 'Snapshot', `${record.quoteSnapshotHash.slice(0, 12)}...`, record.quoteSnapshotHash);
    }
    if (record.inventoryCommitmentSeq != null) {
      pushFact(commonFacts, 'Commitment order', `#${record.inventoryCommitmentSeq}`);
    }

    if (!record.isCurrent) {
      if (isClientConfirmationRecord(record)) {
        pushFact(commonFacts, 'Signed', formatDateTime(record.clientAcceptedAt));
        pushFact(commonFacts, 'Client', record.acceptedClientEmail);
        pushFact(commonFacts, 'Signature', record.signatureMethod);
      } else if (method.includes('manager')) {
        pushFact(commonFacts, 'Approved by', record.approvedByName);
        pushFact(commonFacts, 'Approved', formatDateTime(record.approvedAt));
      }
      return {
        kind: 'historical',
        eyebrow: 'Prior confirmation evidence',
        title: 'Previously confirmed - not currently firm',
        detail: 'This evidence belongs to an earlier commitment epoch. It remains retained, but it does not reserve inventory or count as the current client signature.',
        facts: commonFacts,
      };
    }

    if (isClientConfirmationRecord(record)) {
      pushFact(commonFacts, 'Signed', formatDateTime(record.clientAcceptedAt));
      pushFact(commonFacts, 'Client', record.acceptedClientEmail);
      pushFact(commonFacts, 'Signature', record.signatureMethod);
      return {
        kind: 'client',
        eyebrow: 'Client evidence',
        title: 'Client signed',
        detail: 'The event was confirmed from recorded client acceptance.',
        facts: commonFacts,
      };
    }

    if (method.includes('manager')) {
      pushFact(commonFacts, 'Requested by', record.requestedByName);
      pushFact(commonFacts, 'Requested', formatDateTime(record.requestedAt));
      pushFact(commonFacts, 'Approved by', record.approvedByName);
      pushFact(commonFacts, 'Approved', formatDateTime(record.approvedAt));
      return {
        kind: 'manager',
        eyebrow: 'Approved exception',
        title: 'Confirmed without client signature',
        detail: 'A manager approved this documented confirmation bypass.',
        facts: commonFacts,
      };
    }

    if (method.includes('direct') || method.includes('bypass') || method.includes('manual')) {
      return {
        kind: 'direct',
        eyebrow: 'Documented exception',
        title: 'Confirmed without client signature',
        detail: "An authorized staff member used the location's direct confirmation bypass.",
        facts: commonFacts,
      };
    }

    return {
      kind: 'legacy',
      eyebrow: 'Confirmation evidence',
      title: 'Confirmed - legacy evidence unavailable',
      detail: 'The event is firm, but the stored record does not prove client acceptance.',
      facts: commonFacts,
    };
  }

  if (firm) {
    return {
      kind: 'legacy',
      eyebrow: 'Confirmation evidence',
      title: 'Confirmed - evidence not yet classified',
      detail: 'No client signature or approved bypass record was found. Do not treat this lifecycle status as client acceptance.',
      facts: [],
    };
  }

  const policy = confirmation?.policy ?? 'client_signature_required';
  if (policy === 'direct_bypass_allowed') {
    return {
      kind: 'required',
      eyebrow: 'Location policy',
      title: 'Client signature or documented bypass',
      detail: 'This location permits authorized staff to confirm without a signature when they record a reason.',
      facts: [],
    };
  }
  if (policy === 'manager_bypass_required') {
    return {
      kind: 'required',
      eyebrow: 'Location policy',
      title: 'Client signature or manager approval',
      detail: 'A manager must approve any request to confirm this event without a client signature.',
      facts: [],
    };
  }
  return {
    kind: 'required',
    eyebrow: 'Location policy',
    title: 'Client signature required',
    detail: 'Send the quote to the client. The event confirms when the client accepts the quote.',
    facts: [],
  };
}

export default function EventConfirmationEvidence({
  confirmation,
  eventStatus,
  clientAcceptance,
  loading = false,
  error = null,
  compact = false,
  className = '',
}: EventConfirmationEvidenceProps) {
  const view = buildEventConfirmationEvidenceView({
    confirmation,
    eventStatus,
    clientAcceptance,
    loading,
    error,
  });
  const classes = [
    'event-confirmation-evidence',
    compact ? 'event-confirmation-evidence--compact' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <section className={classes} data-kind={view.kind} aria-label="Event confirmation evidence">
      <span className="event-confirmation-evidence__marker" aria-hidden="true" />
      <div className="event-confirmation-evidence__content">
        <p className="event-confirmation-evidence__eyebrow">{view.eyebrow}</p>
        <strong className="event-confirmation-evidence__title">{view.title}</strong>
        {!compact || view.kind === 'pending' || view.kind === 'legacy' || view.kind === 'r2' ? (
          <p className="event-confirmation-evidence__detail">{view.detail}</p>
        ) : null}
        {view.facts.length > 0 ? (
          <dl className="event-confirmation-evidence__facts">
            {view.facts.map((fact, index) => (
              <div key={`${fact.label}-${index}`}>
                <dt>{fact.label}</dt>
                <dd title={fact.title}>{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </section>
  );
}
